// The bargaining arithmetic, mirroring backend/pricing.py.
//
// Duplicated deliberately. The shop works offline, so the till has to draw a
// floor and the form has to suggest a tag price with no server to ask. The
// server still decides -- these numbers are a courtesy, and
// backend/pricing.py is the rule. Any change here needs the same change there.

export const BASIS_PIECE = "piece";
export const BASIS_DOZEN = "dozen";
export const BASIS_WEIGHT = "weight";

export const PIECES_PER_DOZEN = 12;

// Five, not fifty. Rounding a Rs 94 thali's 10% floor up to the nearest fifty
// lands on 150, turning a 10% minimum margin into 60%.
export const FLOOR_ROUNDING = 5;

export function piecesPerPack(packSize) {
  return packSize > 0 ? Number(packSize) : PIECES_PER_DOZEN;
}

/**
 * The least this item may go for, per piece.
 *
 * An explicit minPrice wins, for clearance stock. A cost of zero means the shop
 * does not know what it paid, so there is nothing to protect.
 */
export function deriveFloor(costPrice, minMarginPercent, minPrice) {
  if (minPrice !== null && minPrice !== undefined && minPrice !== "") {
    return Number(minPrice);
  }
  const cost = Number(costPrice) || 0;
  if (cost <= 0) return 0;
  const raw = cost * (1 + (Number(minMarginPercent) || 0) / 100);
  // Rounded before the ceiling: 100 * 1.1 is 110.00000000000001, and Math.ceil
  // then jumps a whole step, floor 115 on an item that should floor at 110.
  // It bites the round numbers a shop deals in most. Must match
  // derive_floor in backend/pricing.py.
  const steps = Math.round((raw / FLOOR_ROUNDING) * 1e9) / 1e9;
  return Math.ceil(steps) * FLOOR_ROUNDING;
}

/**
 * The same floor in whatever unit the customer is being charged.
 *
 * Returns null when it cannot be worked out -- a weight sale before anything has
 * been weighed -- so callers can say so rather than show a wrong number.
 */
export function floorForBasis(floorPerPiece, basis, { packSize, weightG } = {}) {
  if (!floorPerPiece || floorPerPiece <= 0) return 0;
  if (basis === BASIS_PIECE) return floorPerPiece;
  if (basis === BASIS_DOZEN) return floorPerPiece * piecesPerPack(packSize);
  if (basis === BASIS_WEIGHT) {
    if (!weightG || weightG <= 0) return null;
    return floorPerPiece / (weightG / 1000);
  }
  return null;
}

/** How many pieces leave the shelf. Null when a weight cannot be converted. */
export function piecesSold(basis, basisQuantity, { packSize, weightG } = {}) {
  const qty = Number(basisQuantity) || 0;
  if (basis === BASIS_PIECE) return qty;
  if (basis === BASIS_DOZEN) return qty * piecesPerPack(packSize);
  if (basis === BASIS_WEIGHT) {
    if (!weightG || weightG <= 0) return null;
    return (qty * 1000) / weightG;
  }
  return null;
}

/** A product's markup: its category's if it has one, else the shop's. */
export function markupFor(category, shop) {
  const fromCategory = category?.default_markup;
  if (fromCategory && fromCategory > 0) return Number(fromCategory);
  return Number(shop?.default_markup) || 2;
}

/**
 * Where an agreed price sits between the floor and the tag price.
 *
 * Drives the colour band on the till. "below" is the only one that is actually
 * blocked; "thin" is a nudge, not a refusal -- the shopkeeper may have a reason.
 */
export function priceStanding(rate, { floor, tag }) {
  const r = Number(rate) || 0;
  if (floor > 0 && r < floor) return "below";
  if (floor > 0 && r < floor * 1.15) return "thin";
  if (tag > 0 && r >= tag) return "full";
  return "good";
}
